import { AsyncPipe } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  model,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import dayjs from 'dayjs';
import { round } from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

import { DownloadTemplateButtonComponent } from '@features/download-template-button/download-template-button.component';
import { MultipleVendorSelectComponent } from '@features/multiple-vendor-select/multiple-vendor-select.components';

import { injectListOrganizationsWithTotalsQuery } from '@models/organization/list-organizations-with-totals.query';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { FileManagerComponent } from '@shared/components/file-manager/file-manager.component';
import { FileViewerComponent } from '@shared/components/file-viewer/file-viewer.component';
import { InputComponent } from '@shared/components/input/input.component';
import {
  ConfirmationActionModalComponent,
  ConfirmationActionModalData,
} from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ApiService, FileMetadata } from '@shared/services/api.service';
import {
  BudgetType,
  EntityType,
  EventType,
  GqlService,
  TemplateType,
  TrialImplementationStatus,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationModel } from '@shared/store/organization/organization.store';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgSelectModule,
    ReactiveFormsModule,
    InputComponent,
    CheckboxComponent,
    FormsModule,
    AsyncPipe,
    FileManagerComponent,
    FileViewerComponent,
    DownloadTemplateButtonComponent,
    MultipleVendorSelectComponent,
  ],
  styleUrl: 'budget-upload.component.scss',
  templateUrl: 'budget-upload.component.html',
})
export class BudgetUploadComponent implements AfterViewChecked, AfterViewInit {
  ref = inject(
    CustomOverlayRef<
      unknown,
      { isMultiple?: boolean; onSuccess: (processEventId: string) => void; vendorId?: string }
    >
  );

  private apiService = inject(ApiService);
  private gqlService = inject(GqlService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);

  vendorsQuery = injectListOrganizationsWithTotalsQuery();

  errorMessage = signal('');
  loading = signal(false);

  vendors = computed(() => this.vendorsQuery.data() || []);

  selectedVendorIDs = model<string[]>([]);

  attributesOverride = false;
  budget_version = new FormControl('');
  budgetLines: null | number = null;
  selectedVendor = new FormControl<null | OrganizationModel>(null);
  budgetTemplateFileName$ = this.selectedVendor.valueChanges.pipe(
    map((vendor) => {
      const v = this.vendors().find((v) => v.id === vendor?.id);

      return v?.name ? `${v.name}_VENDOR_BUDGET_TEMPLATE.csv` : 'VENDOR_BUDGET_TEMPLATE.csv';
    })
  );
  budgetTemplateMaxHeight = '';
  budgetTotal: null | number = null;
  bv_descriptions: {
    budget_type?: string;
    budget_version_name?: string;
    vendor_id?: string;
  }[] = [];
  bypassValidation = false;
  discountTotal: null | number = null;
  @ViewChild(FileManagerComponent) fileManager: FileManagerComponent | undefined;
  investigatorTotal: null | number = null;
  metadata: FileMetadata = {};
  passthroughTotal: null | number = null;
  selectedBudgetType = new FormControl('primary');
  selectedTemplate = new FormControl('budget');
  servicesCategories: null | number = null;
  servicesTotal: null | number = null;
  templateTypes = TemplateType;
  @ViewChild(NgSelectComponent) vendorSelect!: NgSelectComponent;

  ngAfterViewInit(): void {
    setTimeout(() => {
      const vendors = this.vendors();

      if (vendors.length && !!this.ref.data?.vendorId) {
        const selectItem = this.vendorSelect.itemsList.items.find(
          (item) => item.value.id === this.ref.data?.vendorId
        );

        if (selectItem) {
          this.vendorSelect.select(selectItem);
        }
      }
    }, 0);
  }

  ngAfterViewChecked(): void {
    this.maxHeightCalcFunction();
  }

  constructor() {
    this.selectedBudgetType.valueChanges
      .pipe(startWith(this.selectedBudgetType.value as string), takeUntilDestroyed())
      .subscribe((selectedBudgetType) => {
        if (selectedBudgetType === 'secondary') {
          this.selectedTemplate.setValue(null, { emitEvent: false });
        } else if (this.selectedTemplate.value == null) {
          this.selectedTemplate.setValue('budget', { emitEvent: false });
        }
      });

    this.selectedTemplate.valueChanges
      .pipe(startWith(this.selectedTemplate.value as string), takeUntilDestroyed())
      .subscribe(() => {
        if (this.selectedBudgetType.value === 'secondary') {
          this.selectedBudgetType.setValue('primary', { emitEvent: false });
        }
      });

    if (this.ref.data?.isMultiple) {
      this.budget_version.setValue(`${dayjs().format('YYYY.MM.DD-HHmmss')}`);

      this.launchDarklyService
        .select$((flags) => flags.multi_vendor_budget_template)
        .pipe(takeUntilDestroyed())
        .subscribe((isEnabled) => {
          if (!isEnabled) {
            this.ref.close();
          }
        });
    }
  }

  downloadInstructions() {
    window.open('https://auxilius.atlassian.net/l/cp/cM80sAyS', '_blank');
  }

  async explicitAcknowledgement() {
    const trial = this.mainQuery.getSelectedTrial();

    if (
      !trial?.implementation_status ||
      ![
        TrialImplementationStatus.IMPLEMENTATION_STATUS_LIVE,
        TrialImplementationStatus.IMPLEMENTATION_STATUS_PARALLEL_CLOSE,
        TrialImplementationStatus.IMPLEMENTATION_STATUS_USER_ACCEPTANCE_TESTING,
      ].includes(trial.implementation_status)
    ) {
      return false;
    }

    const { data: actual } = await firstValueFrom(
      this.gqlService.listTrialExpenseTimeline$({
        by_vendor: false,
        expense_type_id: 'EXPENSE_WP',
        nz_expenses_only: false,
      })
    );

    if (!actual?.length) {
      return false;
    }

    const ref = this.overlayService.openPopup<
      ConfirmationActionModalData,
      boolean,
      ConfirmationActionModalComponent
    >({
      data: {
        keywordToExecuteAction: 'Replace Current Budget',
        message:
          'This budget upload will replace this Vendor Contract’s current budget that contains actuals. Proceeding with this upload will impact your budget, actuals to date, accruals, forecast, and all budget-driven data for this Vendor Contract.',
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Replace Current Budget Containing Actuals?',
        primaryButton: {
          label: 'Replace Current Budget',
          variant: 'negative',
        },
      },
    });

    return !(await firstValueFrom(ref.afterClosed$)).data;
  }

  getFilePath(vendorSub: string) {
    const trialId = this.mainQuery.getValue().trialKey;

    return `trials/${trialId}/vendors/${vendorSub}/budget/`;
  }

  hasMoreThanTwoNumbersAfterTheDecimal(val: null | number) {
    if (val && !Number.isNaN(val)) {
      return Number(val) !== round(val, 2);
    }

    return false;
  }

  isBudgetLinesInvalid() {
    if (this.budgetLines) {
      return !this.isWholePositiveNumber(this.budgetLines);
    }

    return false;
  }

  isBudgetTotalInvalid() {
    return this.hasMoreThanTwoNumbersAfterTheDecimal(this.budgetTotal);
  }

  isDiscountTotalInvalid() {
    return this.hasMoreThanTwoNumbersAfterTheDecimal(this.discountTotal);
  }

  isInvestigatorTotalInvalid() {
    return this.hasMoreThanTwoNumbersAfterTheDecimal(this.investigatorTotal);
  }

  isPassthroughTotalInvalid() {
    return this.hasMoreThanTwoNumbersAfterTheDecimal(this.passthroughTotal);
  }

  isServicesCategoriesInvalid() {
    if (this.servicesCategories) {
      return !this.isWholePositiveNumber(this.servicesCategories);
    }

    return false;
  }

  isServiceTotalInvalid() {
    return this.hasMoreThanTwoNumbersAfterTheDecimal(this.servicesTotal);
  }

  isWholePositiveNumber(val: null | number) {
    if (val && !Number.isNaN(this.budgetTotal)) {
      return val > 0 && val % 1 === 0;
    }

    return false;
  }

  maxHeightCalcFunction() {
    const budgetTemplateElement = document.getElementById('budgetUploadTemplate');

    if (budgetTemplateElement) {
      const getRect = budgetTemplateElement.getBoundingClientRect();
      const bottomPos = getRect.bottom;
      const topPos = getRect.top;
      const window_height = window.innerHeight;

      if (window_height - bottomPos < 60 && window_height - bottomPos > 20) {
        const rounded_num = Math.round(bottomPos - topPos);

        this.budgetTemplateMaxHeight = !this.budgetTemplateMaxHeight
          ? `${rounded_num}px`
          : this.budgetTemplateMaxHeight;
        budgetTemplateElement.style.maxHeight = this.budgetTemplateMaxHeight;
      } else if (window_height - bottomPos <= 20) {
        const rounded_num = Math.round(window_height - 60);

        this.budgetTemplateMaxHeight = !this.budgetTemplateMaxHeight
          ? `${rounded_num}px`
          : this.budgetTemplateMaxHeight;
        budgetTemplateElement.style.maxHeight = this.budgetTemplateMaxHeight;
      } else {
        this.budgetTemplateMaxHeight = '';
        budgetTemplateElement.style.maxHeight = '';
      }
    }
  }

  get multipleVendorUploadDisabled(): boolean {
    return this.loading();
  }

  onDiscountTotalChange() {
    if (this.discountTotal && this.discountTotal > 0) {
      this.discountTotal *= -1;
    }
  }

  // eslint-disable-next-line max-lines-per-function
  onMultipleVendorUpload = async () => {
    this.errorMessage.set('');

    if (!(this.fileManager && !this.loading())) {
      return;
    }

    const files = this.fileManager.fileQuery.getAll();

    const budget_type = BudgetType.BUDGET_PRIMARY;

    if (
      this.bv_descriptions.some(
        (desc) =>
          desc.budget_type === budget_type &&
          desc.budget_version_name?.toLowerCase() ===
            (this.budget_version.value as string).toLowerCase() &&
          desc.vendor_id === this.selectedVendor.value?.id
      )
    ) {
      this.errorMessage.set('Duplicate budget versions not allowed!');

      return;
    }

    if (!files.length) {
      this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

      return;
    }

    if (files.length > 1) {
      this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

      return;
    }

    const match = files[0].bucket_key.match(/\.([^.]+)$/);

    if (match?.[1] !== 'csv') {
      this.errorMessage.set('File type must be a .csv!');

      return;
    }

    this.loading.set(true);

    if (await this.explicitAcknowledgement()) {
      this.loading.set(false);

      return;
    }

    const file = files[0];
    const key = `trials/${this.mainQuery.getValue().trialKey}/${file.bucket_key}`;

    this.fileManager.fileStore.update(file.id, {
      ...file,
      bucket_key: key,
    });

    const fileSuccess = await this.fileManager.fileService.uploadFiles({
      metadata: { admin: '1' },
    });

    if (fileSuccess) {
      const tracking_id = uuidv4();
      const { data, errors, success } = await firstValueFrom(
        this.gqlService.validateEventFile$({
          bucket_key: `public/${key}`,
          entity_id: '',
          entity_type: EntityType.ORGANIZATION,
          payload: JSON.stringify({
            attributes_override: this.attributesOverride,
            budget_type,
            manual_forecast: this.selectedTemplate.value === 'forecast',
            skip_activityno_check: this.bypassValidation,
            user_description: this.budget_version.value,
          }),
          tracking_id,
          type: EventType.MULTI_VENDOR_BUDGET_TEMPLATE_UPLOADED,
        })
      );

      if (success && data?.id) {
        this.overlayService.success(`Budget is processing. Please wait...`);
        this.ref.data?.onSuccess(data.id);
      } else {
        await this.apiService.removeFile(key);
        this.overlayService.error(errors, undefined, true);
      }

      this.ref.close(data);
    }

    this.loading.set(false);
  };

  // eslint-disable-next-line max-lines-per-function
  onUpload = async () => {
    this.errorMessage.set('');

    if (!(this.fileManager && !this.loading())) {
      return;
    }

    const files = this.fileManager.fileQuery.getAll();

    const budget_type =
      this.selectedBudgetType.value === 'primary'
        ? BudgetType.BUDGET_PRIMARY
        : BudgetType.BUDGET_SECONDARY;

    if (
      this.bv_descriptions.some(
        (desc) =>
          desc.budget_type === budget_type &&
          desc.budget_version_name?.toLowerCase() ===
            (this.budget_version.value as string).toLowerCase() &&
          desc.vendor_id === this.selectedVendor.value?.id
      )
    ) {
      this.errorMessage.set('Duplicate budget versions not allowed!');

      return;
    }

    if (!files.length) {
      this.errorMessage.set(MessagesConstants.FILE.NEED_UPLOAD_FILE);

      return;
    }

    if (files.length > 1) {
      this.errorMessage.set(MessagesConstants.FILE.MAX_ONE_FILE);

      return;
    }

    if (!this.selectedVendor.value) {
      this.errorMessage.set('Must select a vendor contract!');

      return;
    }

    if (!this.budget_version.value) {
      this.errorMessage.set('Must define a budget version!');

      return;
    }

    const match = files[0].bucket_key.match(/\.([^.]+)$/);

    if (match?.[1] !== 'csv') {
      this.errorMessage.set('File type must be a .csv!');

      return;
    }

    this.loading.set(true);

    if (await this.explicitAcknowledgement()) {
      this.loading.set(false);

      return;
    }

    const file = files[0];
    const key = `${this.getFilePath(this.selectedVendor.value.id)}${file.bucket_key}`;

    this.fileManager.fileStore.update(file.id, {
      ...file,
      bucket_key: key,
    });

    const fileSuccess = await this.fileManager.fileService.uploadFiles({
      metadata: { admin: '1' },
    });

    if (fileSuccess) {
      const tracking_id = uuidv4();
      const { data, errors, success } = await firstValueFrom(
        this.gqlService.validateEventFile$({
          bucket_key: `public/${key}`,
          entity_id: this.selectedVendor.value.id,
          entity_type: EntityType.ORGANIZATION,
          payload: JSON.stringify({
            attributes_override: this.attributesOverride,
            budget_lines: this.budgetLines,
            budget_total: this.budgetTotal,
            budget_type,
            discount_total: this.discountTotal,
            investigator_total: this.investigatorTotal,
            manual_forecast: this.selectedTemplate.value === 'forecast',
            passthrough_total: this.passthroughTotal,
            services_categories: this.servicesCategories,
            services_total: this.servicesTotal,
            skip_activityno_check: this.bypassValidation,
            user_description: this.budget_version.value,
          }),
          tracking_id,
          type: EventType.BUDGET_TEMPLATE_UPLOADED,
        })
      );

      if (success && data?.id) {
        this.overlayService.success(`Budget is processing. Please wait...`);
        this.ref.data?.onSuccess(data.id);
      } else {
        await this.apiService.removeFile(key);
        this.overlayService.error(errors, undefined, true);
      }

      this.ref.close(data);
    }

    this.loading.set(false);
  };

  async onVendorSelected(vendor: OrganizationModel) {
    const vendorId = vendor.id;

    if (vendorId) {
      const currentTimeStamp = `${vendor.name}-${dayjs().format('YYYY.MM.DD-HHmmss')}`;

      this.budget_version.setValue(currentTimeStamp);
      this.loading.set(true);
      const [primary, secondary] = await Promise.all([
        firstValueFrom(this.gqlService.listBudgetVersions$([BudgetType.BUDGET_PRIMARY], vendorId)),
        firstValueFrom(
          this.gqlService.listBudgetVersions$([BudgetType.BUDGET_SECONDARY], vendorId)
        ),
      ]);

      this.bv_descriptions = [];
      (primary.data || []).map((x) => {
        this.bv_descriptions.push({
          budget_type: x.budget_type,
          budget_version_name: x.budget_name,
          vendor_id: x.vendor_id || undefined,
        });

        return null;
      });
      (secondary.data || []).map((x) => {
        this.bv_descriptions.push({
          budget_type: x.budget_type,
          budget_version_name: x.budget_name,
          vendor_id: x.vendor_id || undefined,
        });

        return null;
      });
      this.loading.set(false);
    }
  }

  pathFn: () => string = () => '';

  get uploadDisabled(): boolean {
    return (
      this.isServicesCategoriesInvalid() ||
      this.isBudgetLinesInvalid() ||
      this.isBudgetTotalInvalid() ||
      this.isServiceTotalInvalid() ||
      this.isDiscountTotalInvalid() ||
      this.isPassthroughTotalInvalid() ||
      this.isInvestigatorTotalInvalid() ||
      this.loading()
    );
  }
}
